import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  ImageList,
  ImageListItem,
  Button,
  Checkbox,
  CircularProgress,
  useTheme,
  useMediaQuery,
  Alert,
} from '@mui/material';
import { HistoryOutlined as HistoryOutlinedIcon } from '@mui/icons-material';
import ImageModal from '../../../components/Modal/ImageModal';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCheckSquare,
  faCloudArrowDown,
  faSquare,
} from '@fortawesome/pro-regular-svg-icons';

// Helper to get a unique ID for each image
const getImageId = (image) => image.src;

const VisitImages = ({ images }) => {
  const [openModal, setOpenModal] = useState(false);
  const [currentImageIndexInModal, setCurrentImageIndexInModal] = useState(0);
  const [isLoadingImages, setIsLoadingImages] = useState(true);
  const [selectedImagesSet, setSelectedImagesSet] = useState(new Set());
  const [isDownloading, setIsDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState('');

  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const isMediumScreen = useMediaQuery(theme.breakpoints.between('sm', 'md'));

  // Modal handlers
  const handleOpenImageModal = (image) => {
    // Find the index of the clicked image
    const imageIndex = images.findIndex(
      (img) => getImageId(img) === getImageId(image)
    );
    setCurrentImageIndexInModal(imageIndex >= 0 ? imageIndex : 0);
    setOpenModal(true);
  };

  const handleCloseImageModal = () => {
    setOpenModal(false);
    setCurrentImageIndexInModal(0);
  };

  // Effects
  useEffect(() => {
    // Initial loading state based on images prop
    setIsLoadingImages(!images || images.length === 0);
    if (images && images.length > 0) {
      setIsLoadingImages(false);
    }
    // Reset selections and errors when images prop changes
    setSelectedImagesSet(new Set());
    setDownloadError('');
  }, [images]);

  // Image grid columns
  const getResponsiveCols = () => {
    if (isSmallScreen) return 2;
    if (isMediumScreen) return 3;
    return 4; // Default for larger screens
  };

  // Image selection handlers
  const handleToggleImageSelection = (image) => {
    const imageId = getImageId(image);
    setSelectedImagesSet((prevSelected) => {
      const newSelected = new Set(prevSelected);
      if (newSelected.has(imageId)) {
        newSelected.delete(imageId);
      } else {
        newSelected.add(imageId);
      }
      return newSelected;
    });
  };

  const handleSelectAllImages = () => {
    if (images && images.length > 0) {
      const allImageIds = new Set(images.map(getImageId));
      setSelectedImagesSet(allImageIds);
    }
  };

  const handleDeselectAllImages = () => {
    setSelectedImagesSet(new Set());
  };

  // Download handler
  const handleDownloadSelectedImages = async () => {
    if (selectedImagesSet.size === 0) {
      setDownloadError(
        'Por favor seleccione al menos una imagen para descargar.'
      );
      return;
    }
    setDownloadError('');
    setIsDownloading(true);

    const zip = new JSZip();
    const imagesToDownload = images.filter((img) =>
      selectedImagesSet.has(getImageId(img))
    );

    try {
      const imageFetchPromises = imagesToDownload.map(async (image) => {
        const originalImageUrl = image.src.split('?')[0]; // Get the raw URL
        const response = await fetch(originalImageUrl);
        if (!response.ok) {
          throw new Error(
            `Error al descargar ${originalImageUrl.split('/').pop()}: ${
              response.statusText
            }`
          );
        }
        const blob = await response.blob();

        // Extract full filename (with extension) from URL
        const originalFileName = originalImageUrl.substring(
          originalImageUrl.lastIndexOf('/') + 1
        );

        return { blob, name: originalFileName };
      });

      const fetchedImages = await Promise.all(imageFetchPromises);

      fetchedImages.forEach(({ blob, name }) => {
        zip.file(name, blob);
      });

      const zipBlob = await zip.generateAsync({ type: 'blob' });
      saveAs(zipBlob, 'visit_images.zip'); //TODO: Cambiar nombre de archivo
    } catch (error) {
      console.error('Error preparando descarga:', error);
      setDownloadError(
        `Ocurrió un error durante la descarga: ${error.message}.`
      );
    } finally {
      setIsDownloading(false);
    }
  };

  // Render logic
  const renderContent = () => {
    if (isLoadingImages) {
      return (
        <Box
          display='flex'
          justifyContent='center'
          alignItems='center'
          height='calc(100% - 70px)'
        >
          <CircularProgress />
          <Typography sx={{ ml: 2 }}>Cargando imágenes...</Typography>
        </Box>
      );
    }

    if (!images || images.length === 0) {
      return (
        <Box
          display='flex'
          alignItems='center'
          justifyContent='center'
          flexDirection='column'
          minHeight='200px'
          height='calc(100% - 70px)' // Adjust considering the action bar height
          width='100%'
        >
          <HistoryOutlinedIcon
            color='disabled'
            sx={{ fontSize: '3rem', mb: 1 }}
          />
          <Typography color='text.secondary'>
            {'No se encontraron imágenes'}
          </Typography>
        </Box>
      );
    }

    return (
      <ImageList
        cols={getResponsiveCols()}
        gap={12}
        rowHeight='auto'
        sx={{
          marginTop: '0 !important',
          overflowY: 'auto',
          maxHeight: 'calc(100vh - 280px)',
        }}
      >
        {images.map((image, index) => {
          const imageId = getImageId(image);
          const isSelected = selectedImagesSet.has(imageId);
          const [latitude, longitude] = image.meta_loc
            ? image.meta_loc.split(', ')
            : ['N/A', 'N/A'];

          // Extract image name from src
          let imageName = 'Nombre no disponible';
          if (image.src) {
            const srcParts = image.src.split('?')[0].split('/');
            const fileNameWithExtension = srcParts[srcParts.length - 1];
            if (fileNameWithExtension) {
              imageName = fileNameWithExtension.replace(
                /[^a-zA-Z0-9_.-]/g,
                '_'
              );
            }
          }

          return (
            <ImageListItem
              key={imageId || index}
              sx={{
                border: '2px solid',
                borderColor: isSelected
                  ? theme.palette.primary.main
                  : 'transparent',
                borderRadius: '12px',
                overflow: 'hidden',
                position: 'relative',
                bgcolor:
                  theme.palette.mode === 'dark' ? 'grey.800' : 'grey.100',
                transition: 'border-color 0.3s ease',
                '&:hover .image-item-overlay': { opacity: 1 },
              }}
            >
              <Checkbox
                checked={isSelected}
                onChange={() => handleToggleImageSelection(image)}
                disabled={isDownloading}
                sx={{
                  position: 'absolute',
                  top: 6,
                  right: 6,
                  zIndex: 2,
                  color: isSelected
                    ? theme.palette.primary.main
                    : theme.palette.common.white,
                  backgroundColor: 'rgba(0, 0, 0, 0.35)', // Slightly darker for better visibility
                  '&:hover': {
                    backgroundColor: 'rgba(0, 0, 0, 0.5)',
                  },
                  padding: '3px', // Slightly smaller padding
                  borderRadius: '50%',
                }}
                size='small'
              />
              <Button
                component='div' // Use div to prevent button-in-button issues if ImageListItem is a button
                onClick={() => handleOpenImageModal(image)}
                sx={{
                  position: 'relative',
                  width: '100%',
                  aspectRatio: '1 / 1',
                  padding: 0,
                  display: 'block',
                  borderRadius: '10px',
                  overflow: 'hidden',
                  cursor: 'pointer',
                  '&:hover .hover-info-overlay': { opacity: 1 },
                }}
                aria-label={`Ver imagen ${index + 1}`}
              >
                <img
                  src={`${image.src}?w=248&h=248&fit=crop&auto=format&quality=80`}
                  srcSet={`${image.src}?w=248&h=248&fit=crop&auto=format&quality=80&dpr=2 2x`}
                  alt={`Visita ${index + 1}`}
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    display: 'block',
                    borderRadius: '10px',
                    transition: 'transform 0.3s ease-in-out',
                  }}
                  loading='lazy'
                  onError={(e) => {
                    e.target.src =
                      'https://via.placeholder.com/248?text=No+Disponible';
                    e.target.alt = 'Imagen no disponible';
                  }}
                />
                <Box
                  className='hover-info-overlay' // Unique class name
                  sx={{
                    position: 'absolute',
                    bottom: 0,
                    left: 0,
                    width: '100%',
                    bgcolor: 'rgba(0, 0, 0, 0.75)',
                    color: 'white',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                    padding: '8px',
                    opacity: 0, // Hidden by default, shown on hover (controlled by parent)
                    transition: 'opacity 0.3s ease',
                    borderBottomLeftRadius: '8px',
                    borderBottomRightRadius: '8px',
                    boxSizing: 'border-box',
                  }}
                >
                  <Typography
                    variant='caption'
                    sx={{
                      textAlign: 'center',
                      lineHeight: 1.2,
                      width: '100%',
                      whiteSpace: 'normal',
                      wordBreak: 'break-all',
                      marginBottom: '7px',
                    }}
                    title={imageName}
                  >
                    {imageName}
                  </Typography>

                  <Typography
                    variant='caption'
                    sx={{ textAlign: 'center', lineHeight: 1.2, width: '100%' }}
                  >
                    {latitude !== 'N/A' && longitude !== 'N/A'
                      ? `${parseFloat(latitude).toFixed(4)}, ${parseFloat(
                          longitude
                        ).toFixed(4)}`
                      : 'Ubicación no disp.'}
                  </Typography>
                  {image.meta_date && (
                    <Typography
                      variant='caption'
                      sx={{
                        textAlign: 'center',
                        lineHeight: 1.2,
                        width: '100%',
                      }}
                    >
                      {new Date(image.meta_date).toLocaleDateString('es-ES', {
                        day: '2-digit',
                        month: '2-digit',
                        year: 'numeric',
                      })}
                    </Typography>
                  )}
                </Box>
              </Button>
            </ImageListItem>
          );
        })}
      </ImageList>
    );
  };

  return (
    <Box flex={6} mx={2} display='flex' flexDirection='column' height='120%'>
      {/* Action Bar */}
      <Box
        sx={{
          paddingY: 2,
          display: 'flex',
          gap: 1,
          flexWrap: 'wrap',
          alignItems: 'center',
          borderBottom: '1px solid',
          borderColor: 'divider',
          marginBottom: 2,
          flexShrink: 0,
        }}
      >
        <Button
          variant='outlined'
          onClick={
            images && selectedImagesSet.size === images.length
              ? handleDeselectAllImages
              : handleSelectAllImages
          }
          disabled={isDownloading || !images || images.length === 0}
          size='small'
          startIcon={
            images && selectedImagesSet.size === images.length ? (
              <FontAwesomeIcon icon={faSquare} />
            ) : (
              <FontAwesomeIcon icon={faCheckSquare} />
            )
          }
        >
          {images && selectedImagesSet.size === images.length
            ? 'Ninguno'
            : `Todo (${images ? images.length : 0})`}
        </Button>
        <Button
          variant='outlined'
          onClick={handleDownloadSelectedImages}
          disabled={isDownloading || selectedImagesSet.size === 0}
          startIcon={
            isDownloading ? (
              <CircularProgress size={18} color='inherit' />
            ) : (
              <FontAwesomeIcon icon={faCloudArrowDown} />
            )
          }
          size='small'
        >
          {isDownloading
            ? 'Comprimiendo...'
            : `Descargar (${selectedImagesSet.size})`}
        </Button>
      </Box>

      {/* Error Alert */}
      {downloadError && (
        <Alert
          severity='error'
          sx={{ marginBottom: 2, flexShrink: 0 }}
          onClose={() => setDownloadError('')}
        >
          {downloadError}
        </Alert>
      )}

      {/* Image Grid or Loading/Empty State */}
      <Box flexGrow={1} overflow='hidden'>
        {' '}
        {/* Allow this Box to take remaining space and handle overflow */}
        {renderContent()}
      </Box>

      {/* Image Modal */}
      {images && images.length > 0 && (
        <ImageModal
          open={openModal}
          onClose={handleCloseImageModal}
          images={images}
          currentImageIndex={currentImageIndexInModal}
        />
      )}
    </Box>
  );
};

export default VisitImages;
